import { action, computed } from 'mobx';

import * as constants from '../../../constants/constants';
import { parseFloatI18n } from "@pes-ui/utils";
import { sendAnalytics } from "../../../utils/analytics";
import {
    getLookupValueProductLists,
    getLookupValueAddCartCTAType,
    setContextValue,
    CONTEXT_MY_PRODUCTS_PRODUCT_DATA
} from '../../../common/analytics/index';

/**
 * @typedef {import('../../../types').RelatedProductsProduct} RelatedProductsProduct
 * @typedef {import('../../../types').MyProductsAPI} MyProductsAPI
 * @typedef {import('../../../types').ProductData} ProductData
 * @typedef {import('../../../types').ProductsFullResponse} ProductsFullResponse
 * @typedef {import('../../../types').Product} Product
 */

/**
 * @param {ProductsFullResponse} response
 * @returns {Product}
 */
const getProductFromList = (response) => response.lists[0].products[0];

export default class RelatedProductStore {
  groupName = '';

  /** @type {string} */
  skuId;

  /** @type {string} */
  commerceRef;

  /** @type {string} */
  productPrice;

  /** @type {string} */
  productCurrency;

  /** @type {string} */
  productDesc;

  /** @type {string} */
  productSelfRef;

  /** @type {string} */
  pictureDocumentReferenceURL;

  /** @type {string} */
  pictureAltText;

  pendingReaction = false;

  card = null;

  /**
   * @param {RelatedProductsProduct} relatedProductObj
   * @param {string} groupId For analytics.
   * @param {*} rootStore
   */
  constructor(relatedProductObj, rootStore, groupId) {
    const { product: { skuId, name, description, imageLink }, url, price, currency, groupName } = relatedProductObj;

    this.groupId = groupId;
    this.groupName = groupName;
    this.skuId = skuId;
    this.commerceRef = name;
    this.productPrice = price;
    this.productCurrency = currency;
    this.productDesc = description;
    this.productSelfRef = url;
    this.pictureDocumentReferenceURL = imageLink.url;
    this.pictureAltText = imageLink.title;

    this.rootStore = rootStore;
    this.cartStore = rootStore.cartStore;
    /** @type {MyProductsAPI} */
    this.myProductsAPI = rootStore.services.myProductsAPI;
  }

  @computed get isFavorite() {
    return this.checkIsFavorite();
  }

  checkIsFavorite() {
    this.card = this.cartStore.getProductCardById(this.skuId);

    return !!this.card;
  }

  /**
   * @param {string} parentSkuId
   */
  toggleFavorite(parentSkuId) {
    if (this.isFavorite) {
      this.removeFromFav();
    } else {
      this.addToFav(parentSkuId);
    }
  }

  /**
   * @param {string} parentSkuId
   */
  addToCart(parentSkuId) {
    if (this.isFavorite) {
        setContextValue(CONTEXT_MY_PRODUCTS_PRODUCT_DATA, {
            gaLocation: `${getLookupValueProductLists('relatedProductsModal', this.groupId)}|${getLookupValueAddCartCTAType('buttonSingleProduct')}`,
            gaVariant: parentSkuId
        });
        this.card.changeQty(1);
        return;
    }

    this.addToFav(parentSkuId);
  }

  @action.bound
  removeFromFav() {
    this.card.remove();
  }

  /**
   * @param {string} parentSkuId
   */
  @action.bound
  addToFav(parentSkuId) {
    if (this.pendingReaction) {
      return;
    }

    const [targetGroup] = this.cartStore.getGroupsByType(constants.groupType.SINGLE);

    if (targetGroup) {
      /** @type {ProductData} */
      const dataToAdd = {
        productId: this.skuId,
        productQty: 1
      };

      this.addCreationDate(dataToAdd, parentSkuId);

      this.pendingReaction = true;

      this.postDataToAdd(dataToAdd, parentSkuId)
        .finally(() => { this.pendingReaction = false; });
    }
  }

  /**
   * @param {ProductData} dataToAdd
   * @param {string} parentSkuId For analytics.
   * @returns {Promise}
   */
  async postDataToAdd(dataToAdd, parentSkuId) {
    try {
      const gaLocation = `${getLookupValueProductLists('relatedProductsModal', this.groupId)}|${getLookupValueAddCartCTAType('buttonSingleProduct')}`;
      const responseData = await this.myProductsAPI.addProducts(dataToAdd);
      const response = await this.myProductsAPI.getProductFullData(this.skuId);
      const product = getProductFromList(response);

      product.price = parseFloatI18n(product.price);
      this.cartStore.addProductToGroup(product);

      const quantity = 1;
      const eventType = constants.ADD_FROM_RELATED_PRODUCT;

      sendAnalytics({
        responseData,
        quantity,
        eventType,
        productData: this,
        parentSkuId,
        additionalData: { gaLocation }
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * This method adds CreationDate for request for ordering just after Parent.
   *
   * @param {ProductData} dataToAdd
   * @param {string} parentSkuId
   */
  addCreationDate(dataToAdd, parentSkuId) {
    const { sortAllByCreationDate } = this.rootStore.cartStore;
    const parentElementIndex = sortAllByCreationDate.findIndex((item) => item.ID === parentSkuId);
    const parentElement = sortAllByCreationDate[parentElementIndex];
    let creationDate = parentElement ? parentElement.creationDate : 0;

    if (creationDate) {
      if (parentElementIndex < sortAllByCreationDate.length - 1) {
        creationDate = sortAllByCreationDate[parentElementIndex + 1].creationDate + 1;
      } else {
        creationDate = parentElement.creationDate - 100;
      }

      Object.assign(dataToAdd, { creationDate });
    }
  }
}